import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import axios from "axios";
import type { ClipboardEvent, DragEvent, Ref } from "react";
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ApiError, ApiResponse } from "@entrilia/shared/api/types";
import type { ArtifactThemeMode } from "@entrilia/shared/artifactRuntime/protocol";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { StateAction } from "@entrilia/shared/types";
import { parseJsonObject } from "@entrilia/shared/utilities/objectHelper";
import biApi from "../../../../../api/biApi";
import type {
  SmartViewArtifactAgentResult,
  SmartViewArtifactVersionSummary,
} from "../../../../../api/types/smartViewArtifactsTypes";
import ArtifactHint from "../ArtifactHint";
import type { ArtifactCheckProblems, ArtifactCheckResult, ArtifactRuntimeError } from "../canvas/checkResult";
import { needsFix } from "../canvas/checkResult";
import ArtifactAgentAvatar from "./ArtifactAgentAvatar";
import ArtifactAgentComposer from "./ArtifactAgentComposer";
import ArtifactAgentImageThumbnail from "./ArtifactAgentImageThumbnail";
import ArtifactAgentModePicker from "./ArtifactAgentModePicker";
import ArtifactAgentReply from "./ArtifactAgentReply";
import ArtifactAgentSavedReply from "./ArtifactAgentSavedReply";
import ArtifactAgentTurn from "./ArtifactAgentTurn";
import ArtifactAgentUserMessage from "./ArtifactAgentUserMessage";
import type { AgentChatMessage, AgentChatState, AgentFixRequest, AgentFixTrigger, AgentTurn } from "./agentChatState";
import {
  addCheckResultAction,
  addProgressAction,
  decideCompare,
  decideRuntimeErrorFix,
  failTurnAction,
  finishTurnAction,
  getAnsweredTurnId,
  getCompareMode,
  getRunningTurn,
  initialAgentChatState,
  isTurnRunning,
  offerFixAction,
  PAGE_RELOADED_LINE,
  reloadHistoryAction,
  skipCompareAction,
  startCompareTurnAction,
  startFixTurnAction,
  startTurnAction,
  stopTurnAction,
  toChatHistory,
  toFixRequest,
  toSendRequest,
  TURN_FAILED_MESSAGE,
} from "./agentChatState";
import {
  describeImageSizeError,
  isWithinImageSizeLimit,
  MAX_IMAGE_COUNT,
  selectImagesToAttach,
} from "./imageAttachments";
import { measurePictureColours } from "./measurePictureColours";
import { getPictureMode } from "./pictureMode";
import { resizeImageAttachment } from "./resizeImageAttachment";
import useArtifactAgentMode from "./useArtifactAgentMode";
import useCompareLostOnReload from "./useCompareLostOnReload";

export interface ArtifactAgentPanelHandle {
  addCheckResult: (result: ArtifactCheckResult) => boolean;
  reportRuntimeError: (version: number, error: ArtifactRuntimeError) => boolean;
  getCompareMode: (version: number) => ArtifactThemeMode | undefined;
}

interface Props {
  ref?: Ref<ArtifactAgentPanelHandle>;
  artifactId: string;
  latestVersion: number;
  viewedVersion: number;
  versions: SmartViewArtifactVersionSummary[] | undefined;
  versionsError: ApiError | undefined;
  focusInput?: boolean;
  isCollapsed?: boolean;
  onVersionSaved: (version: number) => Promise<void>;
  onArtifactChanged: () => Promise<void>;
  onTurnRunningChange: (isRunning: boolean) => void;
  getBuilderView: () => string | undefined;
}

interface AttachedImage {
  file: File;
  previewUrl: string;
}

const COMPARE_RENDER_FILE_NAME = "dashboard.jpg";

const getSavedAt = (
  { result, endedAt }: AgentTurn,
  versions: SmartViewArtifactVersionSummary[]
): string | undefined => {
  if (result.status !== "saved") {
    return undefined;
  }
  const createdAt = versions.find(({ version }) => version === result.version)?.createdAt;
  return createdAt ?? (endedAt !== undefined ? new Date(endedAt).toISOString() : undefined);
};

const getPastedImageFiles = (clipboardData: DataTransfer | null): File[] =>
  Array.from(clipboardData?.items ?? [])
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

const isCancellation = (error: unknown): boolean =>
  axios.isCancel(error) || (error instanceof Error && error.name === "AbortError");

const nothingElseHasFocus = (input: HTMLElement | null): boolean =>
  document.activeElement === null || document.activeElement === document.body || document.activeElement === input;

// Under the fetch adapter a non-2xx response body is still an unread stream.
const readErrorMessage = async (error: unknown): Promise<string> => {
  const body = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (!(body instanceof ReadableStream)) {
    return TURN_FAILED_MESSAGE;
  }
  const text = await new Response(body).text().catch(() => "");
  return parseJsonObject<ApiResponse<unknown>>(text)?.error?.message || TURN_FAILED_MESSAGE;
};

const AgentChatWelcome = () => (
  <Stack spacing={2} sx={{ flex: 1, alignItems: "center", justifyContent: "center", px: 4, textAlign: "center" }}>
    <ArtifactAgentAvatar size="large" />
    <Stack spacing={0.75} sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Hi! I&apos;m your AI Assistant.
      </Typography>
      <Typography color="textSecondary">
        Describe the dashboard you need. I&apos;ll build it and save every change as a new version.
      </Typography>
    </Stack>
  </Stack>
);

interface HistoryMessageProps {
  message: AgentChatMessage;
  caption?: string;
}

const HistoryMessage = ({ message, caption }: HistoryMessageProps) => {
  if (message.role === "user") {
    return <ArtifactAgentUserMessage content={message.content} />;
  }
  return (
    <ArtifactAgentReply>
      <ArtifactAgentSavedReply
        summary={message.content}
        notCopied={message.notCopied}
        version={message.version}
        savedAt={message.savedAt}
      />
      {caption && (
        <Typography variant="caption" color="textSecondary">
          {caption}
        </Typography>
      )}
    </ArtifactAgentReply>
  );
};

interface AttachedImageListProps {
  images: AttachedImage[];
  isPreparing: boolean;
  disabled: boolean;
  onRemove: (previewUrl: string) => void;
}

const AttachedImageList = ({ images, isPreparing, disabled, onRemove }: AttachedImageListProps) => (
  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
    {images.map((image) => (
      <ArtifactAgentImageThumbnail
        key={image.previewUrl}
        previewUrl={image.previewUrl}
        name={image.file.name}
        disabled={disabled}
        onRemove={() => onRemove(image.previewUrl)}
      />
    ))}
    {isPreparing && (
      <Stack
        sx={{
          width: 56,
          height: 56,
          alignItems: "center",
          justifyContent: "center",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <CircularProgress size={20} color="secondary" aria-label="Preparing images" />
      </Stack>
    )}
  </Stack>
);

const ArtifactAgentPanel = ({
  ref,
  artifactId,
  latestVersion,
  viewedVersion,
  versions,
  versionsError,
  focusInput,
  isCollapsed,
  onVersionSaved,
  onArtifactChanged,
  onTurnRunningChange,
  getBuilderView,
}: Props) => {
  const { addEffect, removeEffect } = useIntercomContext();
  const { sendNotificationError } = useNotificationContext();
  const [chatState, setChatState] = useState(initialAgentChatState);
  // Both frames can report an error before a re-render, so fix decisions read the latest state from here.
  const chatStateRef = useRef(chatState);
  const abortControllerRef = useRef<AbortController>(undefined);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isReloading, setIsReloading] = useState(false);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [preparingImageCount, setPreparingImageCount] = useState(0);
  const { agentMode, setAgentMode, isThoroughAvailable } = useArtifactAgentMode();
  const attachedImagesRef = useRef(attachedImages);
  attachedImagesRef.current = attachedImages;
  const answeredTurnId = useMemo(() => getAnsweredTurnId(chatState), [chatState]);
  const isCompareLostOnReload = useCompareLostOnReload(artifactId, chatState.pendingCompare !== undefined);

  useEffect(() => {
    if (isCollapsed) {
      return;
    }
    addEffect("hide");
    return () => removeEffect("hide");
  }, [isCollapsed, addEffect, removeEffect]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  useEffect(() => () => attachedImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl)), []);

  useImperativeHandle(ref, () => ({
    addCheckResult: handleCheckResult,
    reportRuntimeError: handleRuntimeError,
    getCompareMode: getPendingCompareMode,
  }));

  useEffect(() => {
    if (isCollapsed) {
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [versions, chatState.turns, isCollapsed]);

  useEffect(() => {
    if (answeredTurnId !== undefined && nothingElseHasFocus(inputRef.current)) {
      inputRef.current?.focus();
    }
  }, [answeredTurnId]);

  const isRunning = isTurnRunning(chatState);
  const pendingImageCount = chatState.pendingClarification?.images.length ?? 0;

  const updateChatState = (action: StateAction<AgentChatState>): boolean => {
    const previous = chatStateRef.current;
    chatStateRef.current = action(previous);
    setChatState(chatStateRef.current);
    return chatStateRef.current !== previous;
  };

  const removeAttachedImages = (files: File[]) => {
    attachedImagesRef.current
      .filter((image) => files.includes(image.file))
      .forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setAttachedImages((prev) => prev.filter((image) => !files.includes(image.file)));
  };

  const runTurn = async (startAction: StateAction<AgentChatState>, sentAttachedImages: File[] = []) => {
    if (isTurnRunning(chatStateRef.current)) {
      return;
    }
    updateChatState(startAction);
    const turn = getRunningTurn(chatStateRef.current);
    if (!turn) {
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    onTurnRunningChange(true);

    try {
      let result: SmartViewArtifactAgentResult | undefined;
      const request = toSendRequest(turn, latestVersion, agentMode, getBuilderView());
      const measuredColours = await measurePictureColours(request.images ?? []);
      for await (const event of biApi.smartViewArtifacts.sendMessageStream(
        artifactId,
        { ...request, measuredColours },
        controller.signal
      )) {
        if (event.type === "progress") {
          updateChatState(addProgressAction(event.message));
        } else if (event.type === "result") {
          result = event.data;
        }
      }
      if (!result) {
        updateChatState(failTurnAction(TURN_FAILED_MESSAGE));
        return;
      }
      if (result.success && result.version !== null) {
        await onVersionSaved(result.version);
      }
      updateChatState(
        finishTurnAction(result, { agentMode: request.mode, pictureMode: getPictureMode(measuredColours) })
      );
      if (chatStateRef.current.turns.find(({ id }) => id === turn.id)?.result.status !== "failed") {
        removeAttachedImages(sentAttachedImages);
      }
    } catch (error) {
      if (isCancellation(error)) {
        updateChatState(stopTurnAction());
        return;
      }
      logError(error, "[ArtifactAgentPanel] sendMessageStream");
      updateChatState(failTurnAction(await readErrorMessage(error)));
    } finally {
      onTurnRunningChange(false);
    }
  };

  const handleAnswer = (answer: string) => {
    runTurn(startTurnAction(answer));
  };

  const handleComposerSend = (prompt: string) => {
    const images = attachedImages.map((image) => image.file);
    runTurn(startTurnAction(prompt, images), images);
  };

  const sendFix = (fix: AgentFixRequest, trigger: AgentFixTrigger) => {
    runTurn(startFixTurnAction(fix, trigger));
  };

  const handleFix = (fix: AgentFixRequest) => sendFix(fix, "requested");

  const handleProblems = (version: number, found: ArtifactCheckProblems): boolean => {
    const decision = decideRuntimeErrorFix(chatStateRef.current, version, latestVersion);
    if (decision === "automatic") {
      sendFix(toFixRequest(version, found), "automatic");
      return false;
    }
    if (decision !== "offer") {
      return false;
    }
    return updateChatState(offerFixAction(version, found));
  };

  const handleRuntimeError = (version: number, error: ArtifactRuntimeError): boolean =>
    handleProblems(version, { errors: [error], problems: [], warnings: [] });

  const getPendingCompareMode = (version: number): ArtifactThemeMode | undefined =>
    getCompareMode(chatStateRef.current, version, latestVersion);

  const startOrSkipCompare = (result: ArtifactCheckResult): boolean => {
    const decision = decideCompare(chatStateRef.current, result, latestVersion);
    if (decision.kind === "start") {
      const render = new File([decision.render], COMPARE_RENDER_FILE_NAME, { type: "image/jpeg" });
      runTurn(startCompareTurnAction(render, result.version));
      return true;
    }
    return decision.kind === "skip" && updateChatState(skipCompareAction(decision.reason));
  };

  const handleCheckResult = (result: ArtifactCheckResult): boolean => {
    const offeredFix = needsFix(result) ? handleProblems(result.version, result) : false;
    const addedNotes = updateChatState(addCheckResultAction(result));
    const handledCompare = startOrSkipCompare(result);
    return offeredFix || addedNotes || handledCompare;
  };

  const handleStopClick = () => abortControllerRef.current?.abort();

  const handleReload = async () => {
    updateChatState(reloadHistoryAction);
    setIsReloading(true);
    await onArtifactChanged();
    setIsReloading(false);
  };

  const attachImages = async (files: File[], imageCount: number) => {
    const { accepted, rejectedMessages } = selectImagesToAttach(imageCount, files);
    rejectedMessages.forEach((message) => sendNotificationError(message));
    setPreparingImageCount((count) => count + accepted.length);
    for (const file of accepted) {
      try {
        const resized = await resizeImageAttachment(file);
        if (!isWithinImageSizeLimit(resized.size)) {
          sendNotificationError(describeImageSizeError(file));
          continue;
        }
        setAttachedImages((prev) => [...prev, { file: resized, previewUrl: URL.createObjectURL(resized) }]);
      } catch (error) {
        logError(error, "[ArtifactAgentPanel] resizeImageAttachment");
        sendNotificationError(`Couldn't read "${file.name}".`);
      } finally {
        setPreparingImageCount((count) => count - 1);
      }
    }
  };

  const handleFilesSelected = (files: File[]) => {
    if (files.length === 0 || isRunning || isReloading) {
      return;
    }
    void attachImages(files, attachedImages.length + preparingImageCount + pendingImageCount);
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const files = getPastedImageFiles(event.clipboardData);
    if (files.length === 0) {
      return;
    }
    event.preventDefault();
    handleFilesSelected(files);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleFilesSelected(Array.from(event.dataTransfer.files));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemoveImage = (previewUrl: string) => {
    URL.revokeObjectURL(previewUrl);
    setAttachedImages((prev) => prev.filter((image) => image.previewUrl !== previewUrl));
  };

  if (!versions) {
    if (versionsError) {
      logError(versionsError, "[ArtifactAgentPanel] listVersions");
      return <DataLoadingFailed title="Failed to load the chat" text={versionsError.message} />;
    }
    return <InlineLoader />;
  }

  const { turns, pendingClarification, draft } = chatState;
  const history = toChatHistory(chatState, versions, latestVersion);
  const lastReplyId = history.filter(({ role }) => role === "assistant").at(-1)?.id;
  const isPreparingImages = preparingImageCount > 0;
  const shownImages = isRunning ? [] : attachedImages;
  const placeholder =
    shownImages.length > 0
      ? `Add a message to send with your ${shownImages.length === 1 ? "image" : "images"}...`
      : "Describe what you need...";

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Stack spacing={2.5} sx={{ flex: 1, overflow: "auto", px: 2, pt: 1, pb: 2 }}>
        {history.length === 0 && turns.length === 0 && <AgentChatWelcome />}
        {history.map((message) => (
          <HistoryMessage
            key={message.id}
            message={message}
            caption={
              isCompareLostOnReload && chatState.nextTurnId === 1 && message.id === lastReplyId
                ? PAGE_RELOADED_LINE
                : undefined
            }
          />
        ))}
        {turns.map((turn) => (
          <ArtifactAgentTurn
            key={turn.id}
            turn={turn}
            savedAt={getSavedAt(turn, versions)}
            isAnswerable={pendingClarification?.turnId === turn.id}
            disabled={isRunning || isReloading}
            onAnswer={handleAnswer}
            onFix={handleFix}
            onReload={handleReload}
          />
        ))}
        <Box ref={messagesEndRef} />
      </Stack>
      <Stack
        spacing={1}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{ px: 1.5, pb: 1.5 }}
      >
        {viewedVersion !== latestVersion && (
          <ArtifactHint
            icon={<HistoryRoundedIcon color="secondary" />}
            text={`You're viewing version ${viewedVersion}. Your message will edit the latest version, ${latestVersion}.`}
          />
        )}
        <ArtifactAgentComposer
          key={draft?.turnId ?? 0}
          placeholder={placeholder}
          attachments={
            (shownImages.length > 0 || isPreparingImages) && (
              <AttachedImageList
                images={shownImages}
                isPreparing={isPreparingImages}
                disabled={isReloading}
                onRemove={handleRemoveImage}
              />
            )
          }
          defaultValue={draft?.text}
          inputRef={inputRef}
          autoFocus={focusInput || draft !== undefined}
          isRunning={isRunning}
          disabled={isRunning || isReloading}
          sendDisabled={isPreparingImages}
          attachDisabled={
            isRunning ||
            isReloading ||
            attachedImages.length + preparingImageCount + pendingImageCount >= MAX_IMAGE_COUNT
          }
          controls={
            isThoroughAvailable && (
              <ArtifactAgentModePicker value={agentMode} disabled={isRunning || isReloading} onChange={setAgentMode} />
            )
          }
          onSend={handleComposerSend}
          onStop={handleStopClick}
          onFilesAdd={handleFilesSelected}
        />
      </Stack>
    </Stack>
  );
};

export default ArtifactAgentPanel;
